import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Star, Calendar, Camera, Eye, Grid, List } from 'lucide-react';

const PhotoGallery = ({ campLocationId, title = "Camp Photos" }) => {
    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [filterType, setFilterType] = useState('all');
    const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);

    const fetchPhotos = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            console.log('PhotoGallery: Fetching photos for camp_location_id:', campLocationId);

            const params = new URLSearchParams({
                camp_location_id: campLocationId,
                order_by: 'display_order',
                order_dir: 'ASC'
            });

            if (filterType !== 'all') {
                params.append('photo_type', filterType);
            }

            if (showFeaturedOnly) {
                params.append('featured_only', 'true');
            }

            const url = `/otgservices/getSiteImages.php?${params}`;
            console.log('PhotoGallery: Fetching from URL:', url);

            const response = await fetch(url);
            console.log('PhotoGallery: Response status:', response.status);

            const data = await response.json();
            console.log('PhotoGallery: API Response:', data);

            if (data.success) {
                const photos = data.data.photos || [];
                console.log('PhotoGallery: Setting photos:', photos);
                setPhotos(photos);
            } else {
                console.error('PhotoGallery: API returned error:', data.error);
                setError(data.error?.message || 'Failed to load photos');
            }
        } catch (err) {
            console.error('PhotoGallery: Network error:', err);
            setError('Network error occurred');
        } finally {
            setLoading(false);
        }
    }, [campLocationId, filterType, showFeaturedOnly]);

    useEffect(() => {
        if (campLocationId) {
            fetchPhotos();
        }
    }, [campLocationId, filterType, showFeaturedOnly, fetchPhotos]);

    const openLightbox = (photo, index) => {
        setSelectedPhoto({ ...photo, index });
    };

    const closeLightbox = useCallback(() => {
        setSelectedPhoto(null);
    }, []);

    const navigateLightbox = useCallback((direction) => {
        if (!selectedPhoto) return;

        const currentIndex = selectedPhoto.index;
        let newIndex;

        if (direction === 'next') {
            newIndex = currentIndex < photos.length - 1 ? currentIndex + 1 : 0;
        } else {
            newIndex = currentIndex > 0 ? currentIndex - 1 : photos.length - 1;
        }

        setSelectedPhoto({ ...photos[newIndex], index: newIndex });
    }, [selectedPhoto, photos]);

    const handleKeyPress = useCallback((e) => {
        if (!selectedPhoto) return;

        if (e.key === 'Escape') {
            closeLightbox();
        } else if (e.key === 'ArrowRight') {
            navigateLightbox('next');
        } else if (e.key === 'ArrowLeft') {
            navigateLightbox('prev');
        }
    }, [selectedPhoto, closeLightbox, navigateLightbox]);

    useEffect(() => {
        if (selectedPhoto) {
            document.addEventListener('keydown', handleKeyPress);
            document.body.style.overflow = 'hidden';
        } else {
            document.removeEventListener('keydown', handleKeyPress);
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.removeEventListener('keydown', handleKeyPress);
            document.body.style.overflow = 'unset';
        };
    }, [selectedPhoto, handleKeyPress]);

    const getPhotoTypeColor = (type) => {
        const colors = {
            general: 'bg-gray-500',
            campsite: 'bg-green-500',
            amenity: 'bg-blue-500',
            view: 'bg-purple-500',
            access_road: 'bg-orange-500',
            entrance: 'bg-yellow-500',
            other: 'bg-red-500'
        };
        return colors[type] || 'bg-gray-500';
    };

    const formatDate = (dateString) => {
        if (!dateString) return null;
        return new Date(dateString).toLocaleDateString();
    };

    if (!campLocationId) {
        return (
            <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-500 text-center">No camp location specified</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="bg-gray-50 p-4 rounded-lg">
                <div className="animate-pulse">
                    <div className="h-6 bg-gray-300 rounded w-1/4 mb-4"></div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {[...Array(8)].map((_, i) => (
                            <div key={i} className="h-32 bg-gray-300 rounded"></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700">Error loading photos: {error}</p>
                <button
                    onClick={fetchPhotos}
                    className="mt-2 px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                >
                    Retry
                </button>
            </div>
        );
    }

    const photoTypes = ['all', 'general', 'campsite', 'amenity', 'view', 'access_road', 'entrance', 'other'];
    const filteredPhotos = photos;

    return (
        <div className="bg-gray-50 p-4 rounded-lg">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2 sm:mb-0">
                    {title} {photos.length > 0 && `(${photos.length})`}
                </h3>

                <div className="flex items-center space-x-2">
                    {/* View Mode Toggle */}
                    <div className="flex bg-gray-200 rounded p-1">
                        <button
                            onClick={() => setViewMode('grid')}
                            className={`p-1 rounded ${viewMode === 'grid' ? 'bg-white shadow' : ''}`}
                            title="Grid View"
                        >
                            <Grid className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => setViewMode('list')}
                            className={`p-1 rounded ${viewMode === 'list' ? 'bg-white shadow' : ''}`}
                            title="List View"
                        >
                            <List className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
                {/* Photo Type Filter */}
                <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded text-sm"
                >
                    {photoTypes.map(type => (
                        <option key={type} value={type}>
                            {type === 'all' ? 'All Types' : type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ')}
                        </option>
                    ))}
                </select>

                {/* Featured Only Toggle */}
                <label className="flex items-center text-sm">
                    <input
                        type="checkbox"
                        checked={showFeaturedOnly}
                        onChange={(e) => setShowFeaturedOnly(e.target.checked)}
                        className="mr-1"
                    />
                    Featured Only
                </label>

                {/* Stats */}
                <span className="text-sm text-gray-500 ml-auto">
                    {filteredPhotos.length} photo{filteredPhotos.length !== 1 ? 's' : ''}
                </span>
            </div>

            {/* Photos Grid/List */}
            {filteredPhotos.length === 0 ? (
                <div className="text-center py-8">
                    <div className="text-gray-400 mb-2">
                        <Camera className="h-12 w-12 mx-auto" />
                    </div>
                    <p className="text-gray-500">No photos found</p>
                    {(filterType !== 'all' || showFeaturedOnly) && (
                        <button
                            onClick={() => {
                                setFilterType('all');
                                setShowFeaturedOnly(false);
                            }}
                            className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
                        >
                            Clear filters
                        </button>
                    )}
                </div>
            ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {filteredPhotos.map((photo, index) => (
                        <div
                            key={photo.id}
                            className="relative group cursor-pointer"
                            onClick={() => openLightbox(photo, index)}
                        >
                            <div className="aspect-square overflow-hidden rounded-lg bg-gray-200">
                                <img
                                    src={photo.thumbnail_url || photo.photo_url}
                                    alt={photo.caption || `Camp photo ${index + 1}`}
                                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                                    loading="lazy"
                                />

                                {/* Overlay */}
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-lg flex items-center justify-center">
                                    <Eye className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>

                                {/* Featured Badge */}
                                {photo.is_featured && (
                                    <div className="absolute top-2 right-2 bg-yellow-500 text-white p-1 rounded">
                                        <Star className="h-3 w-3 fill-current" />
                                    </div>
                                )}

                                {/* Photo Type Badge */}
                                <div className={`absolute top-2 left-2 ${getPhotoTypeColor(photo.photo_type)} text-white px-2 py-1 rounded text-xs`}>
                                    {photo.photo_type}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="space-y-3">
                    {filteredPhotos.map((photo, index) => (
                        <div
                            key={photo.id}
                            className="flex bg-white rounded-lg p-3 hover:shadow-md transition-shadow cursor-pointer"
                            onClick={() => openLightbox(photo, index)}
                        >
                            <div className="w-16 h-16 flex-shrink-0 mr-3">
                                <img
                                    src={photo.thumbnail_url || photo.photo_url}
                                    alt={photo.caption || `Camp photo ${index + 1}`}
                                    className="w-full h-full object-cover rounded"
                                    loading="lazy"
                                />
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="flex items-center mb-1">
                                    <span className={`${getPhotoTypeColor(photo.photo_type)} text-white px-2 py-1 rounded text-xs mr-2`}>
                                        {photo.photo_type}
                                    </span>
                                    {photo.is_featured && (
                                        <Star className="h-4 w-4 text-yellow-500 fill-current mr-2" />
                                    )}
                                    <span className="text-sm text-gray-500">#{photo.display_order}</span>
                                </div>

                                {photo.caption && (
                                    <p className="text-sm text-gray-900 mb-1 truncate">{photo.caption}</p>
                                )}

                                <div className="flex items-center text-xs text-gray-500 space-x-3">
                                    {photo.taken_date && (
                                        <span className="flex items-center">
                                            <Calendar className="h-3 w-3 mr-1" />
                                            {formatDate(photo.taken_date)}
                                        </span>
                                    )}
                                    {photo.camera_info && (
                                        <span className="flex items-center">
                                            <Camera className="h-3 w-3 mr-1" />
                                            {photo.camera_info}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Lightbox */}
            {selectedPhoto && (
                <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
                    <div className="relative w-full h-full flex items-center justify-center p-4">
                        {/* Close Button */}
                        <button
                            onClick={closeLightbox}
                            className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 z-10"
                        >
                            <X className="h-6 w-6" />
                        </button>

                        {/* Navigation Buttons */}
                        {photos.length > 1 && (
                            <>
                                <button
                                    onClick={() => navigateLightbox('prev')}
                                    className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70"
                                >
                                    <ChevronLeft className="h-6 w-6" />
                                </button>
                                <button
                                    onClick={() => navigateLightbox('next')}
                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70"
                                >
                                    <ChevronRight className="h-6 w-6" />
                                </button>
                            </>
                        )}

                        {/* Image */}
                        <img
                            src={selectedPhoto.photo_url}
                            alt={selectedPhoto.caption || `Camp photo ${selectedPhoto.index + 1}`}
                            className="max-w-full max-h-full object-contain"
                        />

                        {/* Image Info */}
                        <div className="absolute bottom-4 left-4 right-4 bg-black bg-opacity-70 text-white p-4 rounded">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center space-x-2">
                                    <span className={`${getPhotoTypeColor(selectedPhoto.photo_type)} px-2 py-1 rounded text-xs`}>
                                        {selectedPhoto.photo_type}
                                    </span>
                                    {selectedPhoto.is_featured && (
                                        <Star className="h-4 w-4 text-yellow-500 fill-current" />
                                    )}
                                </div>
                                <span className="text-sm text-gray-300">
                                    {selectedPhoto.index + 1} of {photos.length}
                                </span>
                            </div>

                            {selectedPhoto.caption && (
                                <p className="text-sm mb-2">{selectedPhoto.caption}</p>
                            )}

                            <div className="flex items-center text-xs text-gray-300 space-x-4">
                                {selectedPhoto.taken_date && (
                                    <span className="flex items-center">
                                        <Calendar className="h-3 w-3 mr-1" />
                                        {formatDate(selectedPhoto.taken_date)}
                                    </span>
                                )}
                                {selectedPhoto.camera_info && (
                                    <span className="flex items-center">
                                        <Camera className="h-3 w-3 mr-1" />
                                        {selectedPhoto.camera_info}
                                    </span>
                                )}
                                <span>Order: {selectedPhoto.display_order}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PhotoGallery;